import axios from 'axios';
import { API_BASE_URL } from '../../config';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

const extensionScriptsService = {
  getAll: () => api.get('/extension-scripts'),

  getOne: (id) => api.get(`/extension-scripts/${id}`),

  create: (data) => api.post('/extension-scripts', data),

  update: (id, data) => api.put(`/extension-scripts/${id}`, data),

  remove: (id) => api.delete(`/extension-scripts/${id}`),

  getVersionHistory: (id) => api.get(`/extension-scripts/${id}/versions`),

  revert: (id, version) => api.post(`/extension-scripts/${id}/revert/${version}`),

  exportAll: () => api.get('/extension-scripts/export'),

  exportOne: (id) => api.get(`/extension-scripts/export/${id}`),

  getExecutions: (id, params) => api.get(`/extension-scripts/${id}/executions`, { params }),

  /** Fetch a single execution record by its own ID (for Run Detail page) */
  getExecution: (executionId) =>
    api.get(`/headless/status/${executionId}`),

  /** Log a video-view audit event (R-3e) */
  recordVideoView: (executionId) =>
    api.post(`/extension-scripts/executions/${executionId}/video-view`),

  /** Alias of getOne with a clearer name for pages that fetch a single script */
  get: (id) => api.get(`/extension-scripts/${id}`),

  startExecution: (id, data) => api.post(`/extension-scripts/${id}/executions`, data),

  completeExecution: (executionId, data) => api.put(`/extension-scripts/executions/${executionId}`, data),

  // Headless execution — Playwright (default executor on the backend)
  runHeadless: (scriptId, data) => api.post(`/headless/run/${scriptId}`, data || {}),
  queueHeadless: (scriptId, data) => api.post(`/headless/queue/${scriptId}`, data || {}),
  getHeadlessStatus: (executionId) => api.get(`/headless/status/${executionId}`),
  getHeadlessJobStatus: (jobId) => api.get(`/headless/job/${jobId}`),
  getHeadlessHistory: (scriptId) => api.get(`/headless/history/${scriptId}`),

  // Headless execution — v5 Chrome extension runner (sibling endpoints).
  // Same request/response shape as runHeadless/queueHeadless so callers can
  // swap engines by routing through `runByExecutor()` below.
  runHeadlessV5: (scriptId, data) => api.post(`/headless/run-v5/${scriptId}`, data || {}),
  queueHeadlessV5: (scriptId, data) => api.post(`/headless/queue-v5/${scriptId}`, data || {}),

  /**
   * Convenience: run a script using the chosen executor mode.
   * @param {string} scriptId
   * @param {'playwright'|'extension-v5'|'extension-v4'} mode
   * @param {object} data  forwarded body (credentialVaultId, variables, recordMode, etc.)
   *
   * v4 + v5 share the /run-v5/ endpoint — the runner branches on the
   * extensionVariant field in the request body.
   */
  runByExecutor: (scriptId, mode, data) => {
    if (mode === 'extension-v4') {
      return api.post(`/headless/run-v5/${scriptId}`, { ...(data || {}), extensionVariant: 'v4' });
    }
    if (mode === 'extension-v5') {
      return api.post(`/headless/run-v5/${scriptId}`, { ...(data || {}), extensionVariant: 'v5' });
    }
    return api.post(`/headless/run/${scriptId}`, data || {});
  },

  queueByExecutor: (scriptId, mode, data) => {
    if (mode === 'extension-v4') {
      return api.post(`/headless/queue-v5/${scriptId}`, { ...(data || {}), extensionVariant: 'v4' });
    }
    if (mode === 'extension-v5') {
      return api.post(`/headless/queue-v5/${scriptId}`, { ...(data || {}), extensionVariant: 'v5' });
    }
    return api.post(`/headless/queue/${scriptId}`, data || {});
  },
};

export { extensionScriptsService };
export default extensionScriptsService;
